import { AnalyticsProvider } from "@/components/analytics/AnalyticsProvider";
import { AppErrorBoundary } from "@/components/common/AppErrorBoundary";
import { OfflineBanner } from "@/components/common/OfflineBanner";
import { NewBookingOfferModal } from "@/components/driver/NewBookingOfferModal";
import { SplashScreenController } from "@/components/splash";
import StripeProvider from "@/components/stripe/stripe-provider";
import { NotificationProvider } from "@/contexts/notification-context";
import { initializeInstallation } from "@/lib/appInstallation";
import { useDesktopLayout } from "@/hooks/use-desktop-layout";
import { isDesktopWebLayout } from "@/lib/desktop-layout";
import { queryClient } from "@/lib/query-client";
import { maybeCheckStoreUpdateDaily } from "@/lib/utils/check-store-update";
import { BottomSheetModalProvider } from "@gorhom/bottom-sheet";
import * as Sentry from "@sentry/react-native";
import { QueryClientProvider } from "@tanstack/react-query";
import { Asset } from "expo-asset";
import { useFonts } from "expo-font";
import * as Notifications from "expo-notifications";
import { SplashScreen, Stack } from "expo-router";
import { StatusBar } from "expo-status-bar";
import { useEffect, useState } from "react";
import {
  AppState,
  Platform,
  StyleSheet,
  View,
  useWindowDimensions,
} from "react-native";
import { GestureHandlerRootView } from "react-native-gesture-handler";
import "react-native-get-random-values";
import {
  MD3LightTheme,
  PaperProvider,
  configureFonts,
} from "react-native-paper";
import { SafeAreaProvider } from "react-native-safe-area-context";
import { Toaster } from "sonner-native";
import "text-encoding-polyfill";
import "whatwg-fetch";
import { AuthProvider, useAuth } from "../contexts/auth-context";
import { useAuthStore } from "../stores/use-auth-store";

const isWeb = Platform.OS === "web";

const FONT_ASSETS = {
  PlusJakartaSans: require("@/assets/fonts/PlusJakartaSans-Regular.ttf"),
  PlusJakartaSansBold: require("@/assets/fonts/PlusJakartaSans-Bold.ttf"),
  // Pre-register icon fonts so vector-icons skips expo-font's FontObserver (6s web timeout).
  ionicons: require("@expo/vector-icons/build/vendor/react-native-vector-icons/Fonts/Ionicons.ttf"),
  "material-community": require("@expo/vector-icons/build/vendor/react-native-vector-icons/Fonts/MaterialCommunityIcons.ttf"),
} as const;

/** expo-font's FontObserver times out at 6s on web and can throw uncaught rejections. */
function useAppFonts(): boolean {
  const [webReady, setWebReady] = useState(!isWeb);
  const [nativeLoaded] = useFonts(
    isWeb
      ? {}
      : {
          PlusJakartaSans: FONT_ASSETS.PlusJakartaSans,
          PlusJakartaSansBold: FONT_ASSETS.PlusJakartaSansBold,
        },
  );

  useEffect(() => {
    if (!isWeb || typeof document === "undefined") {
      return;
    }

    let cancelled = false;

    const onUnhandledRejection = (event: PromiseRejectionEvent) => {
      const message = String(event.reason?.message ?? event.reason ?? "");
      if (message.includes("ms timeout exceeded")) {
        event.preventDefault();
      }
    };
    window.addEventListener("unhandledrejection", onUnhandledRejection);

    (async () => {
      try {
        // Use expo-font's stylesheet id so Font.isLoaded('ionicons') returns true
        // and vector-icons does not call Font.loadAsync / FontObserver.
        const styleId = "expo-generated-fonts";
        let style = document.getElementById(styleId) as HTMLStyleElement | null;
        if (!style) {
          style = document.createElement("style");
          style.id = styleId;
          document.head.appendChild(style);
        }

        const faces = await Promise.all(
          Object.entries(FONT_ASSETS).map(async ([family, moduleId]) => {
            const asset = Asset.fromModule(moduleId);
            await asset.downloadAsync();
            const uri = asset.uri ?? asset.localUri;
            if (!uri) {
              return null;
            }
            return `@font-face{font-family:"${family}";src:url("${uri}");font-display:swap;}`;
          }),
        );

        if (!cancelled && style) {
          const next = faces.filter(Boolean).join("\n");
          if (!style.textContent?.includes("PlusJakartaSans")) {
            style.appendChild(document.createTextNode(next));
          }
        }
      } catch (error) {
        console.warn("Failed to load web fonts", error);
      } finally {
        if (!cancelled) {
          setWebReady(true);
        }
      }
    })();

    return () => {
      cancelled = true;
      window.removeEventListener("unhandledrejection", onUnhandledRejection);
    };
  }, []);

  return isWeb ? webReady : nativeLoaded;
}

if (!isWeb) {
  SplashScreen.preventAutoHideAsync();

  Notifications.setNotificationHandler({
    handleNotification: async () => ({
      shouldShowBanner: true,
      shouldShowList: true,
      shouldPlaySound: true,
      shouldSetBadge: true,
    }),
  });
}

// Patch XMLHttpRequest to fix Android networking timeout issue
// @ts-ignore
if (global.XMLHttpRequest) {
  // @ts-ignore
  const originalOpen = global.XMLHttpRequest.prototype.open;
  // @ts-ignore
  global.XMLHttpRequest.prototype.open = function (
    method: string,
    url: string | URL,
    async?: boolean,
    user?: string,
    password?: string,
  ) {
    const urlString = typeof url === "string" ? url : url.toString();
    originalOpen.call(this, method, urlString, async !== false, user, password);
    // Ensure timeout is always set
    if (this.timeout === undefined || this.timeout === 0) {
      this.timeout = 20000;
    }
  };
}

Sentry.init({
  dsn: process.env.EXPO_PUBLIC_SENTRY_DSN,

  // Adds more context data to events (IP address, cookies, user, etc.)
  // For more information, visit: https://docs.sentry.io/platforms/react-native/data-management/data-collected/
  sendDefaultPii: true,

  // Enable Logs
  enableLogs: true,

  // Configure Session Replay
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1,
  integrations: [
    Sentry.mobileReplayIntegration(),
    Sentry.feedbackIntegration(),
  ],

  // uncomment the line below to enable Spotlight (https://spotlightjs.com)
  // spotlight: __DEV__,
});

function RootLayoutNav() {
  const { user, token, theme } = useAuth();
  const { hasCompletedOnboarding, _hasHydrated } = useAuthStore();
  const loaded = useAppFonts();
  const { width } = useWindowDimensions();
  const desktop = isDesktopWebLayout(Platform.OS, width);

  const isLoggedIn = !!token && !!user;
  const isVerified = !!user?.email_verified_at;
  const allowAccess = isWeb || hasCompletedOnboarding;

  useEffect(() => {
    if (!_hasHydrated && !loaded) return;

    (async () => {
      const freshInstall = await initializeInstallation();

      if (freshInstall) {
        useAuthStore.setState({
          user: null,
          token: null,
          activeBooking: null,
          hasCompletedOnboarding: false,
        });

        useAuthStore.persist.clearStorage();
        useAuthStore.persist.rehydrate();
      }

      SplashScreen.hideAsync();
      void maybeCheckStoreUpdateDaily();
    })();
  }, [_hasHydrated, loaded]);

  useEffect(() => {
    if (isWeb) {
      return;
    }

    const subscription = AppState.addEventListener("change", (nextState) => {
      if (nextState === "active") {
        void maybeCheckStoreUpdateDaily();
      }
    });

    return () => subscription.remove();
  }, []);

  if (isWeb ? !loaded : !_hasHydrated && !loaded) {
    return null;
  }

  return (
    <View style={styles.webPageWrapper}>
      <StatusBar style="auto" animated />
      <OfflineBanner />
      <View
        style={[
          styles.ipadContainer,
          {
            width: desktop ? "100%" : width >= 1024 ? 1024 : width,
            ...(desktop ? { backgroundColor: theme.background, boxShadow: "none" } : null),
          },
        ]}
      >
        <AppErrorBoundary>
          <Stack
            screenOptions={{
              headerShown: false,
              headerBackTitle: "Back",
              headerStyle: { backgroundColor: theme.background },
            }}
          >
            <Stack.Protected guard={!isWeb && !hasCompletedOnboarding}>
              <Stack.Screen
                name="onboarding"
                options={{ headerShown: false }}
              />
            </Stack.Protected>
            <Stack.Protected guard={isLoggedIn && !isVerified && allowAccess}>
              <Stack.Screen name="verify-email" />
            </Stack.Protected>
            <Stack.Protected guard={isLoggedIn && isVerified && allowAccess}>
              <Stack.Screen name="(tabs)" />
              <Stack.Screen name="complete-oauth" />
              <Stack.Screen name="complete-registration" />
              <Stack.Screen name="profile/edit" />
              <Stack.Screen name="profile/license" />
              <Stack.Screen name="profile/change-password" />
              <Stack.Screen name="vehicles/create" />
              <Stack.Screen name="vehicles/[id]" />
              <Stack.Screen name="vehicles/[id]/edit" />
              <Stack.Screen name="bookings/[id]" />
              <Stack.Screen name="bookings/[id]/live-tracking" />
              <Stack.Screen name="bookings/[id]/live-driving" />
              <Stack.Screen name="bookings/[id]/quotes" />
              <Stack.Screen name="bookings/[id]/chat" />
              <Stack.Screen name="bookings/[id]/search-driver" />
              <Stack.Screen name="bookings/[id]/vehicle-inspection" />
              <Stack.Screen name="rate-booking/[id]" />
              <Stack.Screen name="driver-booking-response/[id]" />
              <Stack.Screen name="customer/create-request/step1" />
              <Stack.Screen name="customer/create-request/step2" />
              <Stack.Screen name="customer/create-request/step3" />
              <Stack.Screen name="customer/create-request/step4" />
              <Stack.Screen name="customer/create-request/summary" />
              <Stack.Screen name="panic" />
            </Stack.Protected>
            <Stack.Protected guard={!isLoggedIn && allowAccess}>
              <Stack.Screen name="(auth)" />
            </Stack.Protected>
          </Stack>
        </AppErrorBoundary>
      </View>
    </View>
  );
}

const fontConfig = {
  fontFamily: "PlusJakartaSans",
  titleSmall: {
    letterSpacing: 0.101,
  },
  labelLarge: {
    letterSpacing: 0.101,
  },
};

const theme = {
  ...MD3LightTheme,
  fonts: configureFonts({ config: fontConfig, isV3: true }),
};

export default Sentry.wrap(function RootLayout() {
  const desktop = useDesktopLayout();

  return (
    <QueryClientProvider client={queryClient}>
      <GestureHandlerRootView style={{ flex: 1, backgroundColor: "#1e1e1e" }}>
        <SafeAreaProvider>
          <PaperProvider theme={theme}>
            <StripeProvider>
              <AuthProvider>
                <AnalyticsProvider>
                  <NotificationProvider>
                      <BottomSheetModalProvider>
                        <SplashScreenController />
                        <RootLayoutNav />
                        <NewBookingOfferModal />
                        <Toaster
                          toastOptions={
                            desktop
                              ? { style: styles.desktopToast }
                              : undefined
                          }
                        />
                      </BottomSheetModalProvider>
                  </NotificationProvider>
                </AnalyticsProvider>
              </AuthProvider>
            </StripeProvider>
          </PaperProvider>
        </SafeAreaProvider>
      </GestureHandlerRootView>
    </QueryClientProvider>
  );
});

const styles = StyleSheet.create({
  webPageWrapper: {
    flex: 1,
    backgroundColor: "#1e1e1e", // Dark desktop background color
    justifyContent: "center",
    alignItems: "center",
  },
  ipadContainer: {
    flex: 1,
    height: "100%",
    backgroundColor: "#ffffff", // App background color
    marginHorizontal: "auto",
    overflow: "hidden",
    boxShadow: "0px 0px 24px rgba(0,0,0,0.4)", // Drop shadow on web
  },
  desktopToast: {
    width: 600,
    alignSelf: "center",
  },
});
